/**
 * @fileoverview Admin Portal Layout Wrapper
 *
 * Top-level layout component for the admin portal.
 * Owns global task/calendar data and the tasks dropdown panel,
 * so the panel works on every admin page (not just Dashboard).
 *
 * Data flow:
 * - useAllTasksForCalendar hook lives here
 * - selectedDate state + derived memos live here
 * - CalendarTasksPanel dropdown renders between navbar and page content
 * - TaskViewerModal renders here (opens when clicking a task in the panel)
 * - TaskModal renders here (opens from TaskViewerModal "Edit" or "New Task")
 * - Date resets to today when panel closes
 *
 * @version 4.0.0
 */

import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import CurrentUserContext from "../contexts/CurrentUserContext";
import AdminNavbar from "./../components/admin/AdminNavbar";
import CalendarTasksPanel from "./../components/admin/CalendarTasksPanel";
import TaskViewerModal from "./../components/admin/TaskViewerModal";
import TaskModal from "./../components/admin/TaskModal";
import AdminDashboard from "../pages/admin/AdminDashboard";
import AdminProjects from "../pages/admin/AdminProjects";
import AdminClients from "../pages/admin/AdminClients";
import AdminVendors from "../pages/admin/AdminVendors";
import AdminCalendar from "../pages/admin/AdminCalendar";
import AdminTools from "../pages/admin/AdminTools";
import AdminProfile from "../pages/admin/AdminProfile";
import AdminUploads from "../pages/admin/AdminUploads";
import AdminSettings from "../pages/admin/AdminSettings";
import AdminProjectDetails from "../pages/admin/AdminProjectDetail/AdminProjectDetails";
import VersionFooter from "../components/VersionFooter";
import { getAdminVersion } from "../config/version";
import { useAllTasksForCalendar } from "../hooks/useAdminDashboard";

/**
 * Admin Portal layout component
 *
 * @param {Object} props
 * @param {Object} props.user - Firebase auth user object
 * @param {Object} props.userData - User data from Team Airtable table
 * @returns {JSX.Element} Admin portal with navbar, routes, and version footer
 */
function AdminPortal({ user, userData }) {
  const location = useLocation();

  // ==================== GLOBAL TEST MODE ====================

  const isITUser = userData?.role === "IT";
  const [showTestAccounts, setShowTestAccounts] = useState(false);

  // ==================== TASKS PANEL STATE ====================

  /** Whether the tasks dropdown is open */
  const [tasksPanelOpen, setTasksPanelOpen] = useState(false);

  /** Currently selected date for the tasks calendar */
  const [selectedDate, setSelectedDate] = useState(new Date());

  /** TaskViewerModal state — view task details (read-only) */
  const [viewerModal, setViewerModal] = useState({
    isOpen: false,
    task: null,
  });

  /** TaskModal state — edit existing task */
  const [editTaskModal, setEditTaskModal] = useState({
    isOpen: false,
    taskId: null,
  });

  /** TaskModal state — create new task */
  const [createTaskModal, setCreateTaskModal] = useState({ isOpen: false });

  // ==================== TASK DATA HOOK ====================

  /** Fetch all tasks for the calendar (previously owned by Dashboard) */
  const { data: tasksData, isLoading: tasksLoading } = useAllTasksForCalendar({
    showTestAccounts,
  });

  // ==================== DERIVED TASK DATA ====================

  /**
   * Normalize tasks with a dueDate field for calendar grouping.
   * Uses endDate if available, falls back to startDate.
   */
  const tasks = useMemo(() => {
    if (!tasksData) return [];
    return tasksData.map((task) => ({
      ...task,
      dueDate: task.endDate || task.startDate,
    }));
  }, [tasksData]);

  /**
   * Group tasks by date string ("YYYY-MM-DD") for calendar dot indicators.
   * @type {Object<string, Array>}
   */
  const tasksByDate = useMemo(() => {
    const grouped = {};
    tasks.forEach((task) => {
      if (task.dueDate) {
        if (!grouped[task.dueDate]) grouped[task.dueDate] = [];
        grouped[task.dueDate].push(task);
      }
    });
    return grouped;
  }, [tasks]);

  /**
   * Tasks filtered to the currently selected date.
   * @type {Array}
   */
  const tasksForSelectedDate = useMemo(() => {
    const dateStr = selectedDate.toISOString().split("T")[0];
    return tasks.filter((task) => task.dueDate === dateStr);
  }, [selectedDate, tasks]);

  // ==================== HANDLERS ====================

  /** Toggle the tasks dropdown panel */
  const toggleTasksPanel = useCallback(() => {
    setTasksPanelOpen((prev) => {
      const willClose = prev === true;
      if (willClose) {
        setSelectedDate(new Date());
      }
      return !prev;
    });
  }, []);

  /** Close the tasks panel and reset date to today */
  const closeTasksPanel = useCallback(() => {
    setTasksPanelOpen(false);
    setSelectedDate(new Date());
  }, []);

  /**
   * Handle task click from CalendarTasksPanel — open TaskViewerModal
   * @param {Object} task - Partial task object from the panel
   */
  const handleTaskClick = useCallback((task) => {
    setTasksPanelOpen(false);
    setSelectedDate(new Date());
    setViewerModal({ isOpen: true, task });
  }, []);

  /**
   * Handle "Edit" button from TaskViewerModal — close viewer, open TaskModal
   */
  const handleEditFromViewer = useCallback(() => {
    const taskId = viewerModal.task?.id;
    setViewerModal({ isOpen: false, task: null });
    if (taskId) {
      setEditTaskModal({ isOpen: true, taskId });
    }
  }, [viewerModal.task]);

  /** Close TaskViewerModal */
  const closeViewerModal = useCallback(() => {
    setViewerModal({ isOpen: false, task: null });
  }, []);

  /** Handle create task from CalendarTasksPanel — open TaskModal for new task */
  const handleCreateTask = useCallback(() => {
    setTasksPanelOpen(false);
    setSelectedDate(new Date());
    setCreateTaskModal({ isOpen: true });
  }, []);

  /** Close edit task modal */
  const closeEditTaskModal = useCallback(() => {
    setEditTaskModal({ isOpen: false, taskId: null });
  }, []);

  /** Close create task modal */
  const closeCreateTaskModal = useCallback(() => {
    setCreateTaskModal({ isOpen: false });
  }, []);

  // Close tasks panel on route change (and reset date)
  useEffect(() => {
    setTasksPanelOpen(false);
    setSelectedDate(new Date());
  }, [location.pathname]);

  // Listen for task edit events from Dashboard alerts (overdue tasks click)
  useEffect(() => {
    /** Handle task edit request dispatched from Dashboard alert modals */
    const handleEditTaskEvent = (e) => {
      setEditTaskModal({ isOpen: true, taskId: e.detail.id });
    };
    window.addEventListener("admin-edit-task", handleEditTaskEvent);
    return () =>
      window.removeEventListener("admin-edit-task", handleEditTaskEvent);
  }, []);

  /**
   * Memoized context value so consumers of CurrentUserContext don't
   * re-render unless `user` or `userData` actually change.
   */
  const currentUserValue = useMemo(
    () => ({ user, userData }),
    [user, userData],
  );

  return (
    <CurrentUserContext.Provider value={currentUserValue}>
    <div className="admin-portal">
      <AdminNavbar
        user={user}
        userData={userData}
        tasksPanelOpen={tasksPanelOpen}
        onToggleTasksPanel={toggleTasksPanel}
        tasksBadgeCount={tasksForSelectedDate.length}
        isITUser={isITUser}
        showTestAccounts={showTestAccounts}
        onToggleTestAccounts={setShowTestAccounts}
        tasksDropdownContent={
          tasksPanelOpen && (
            <>
              <div
                className="admin-nav-tasks-dropdown-overlay"
                onClick={closeTasksPanel}
              />
              <div className="admin-nav-tasks-dropdown">
                <CalendarTasksPanel
                  variant="dropdown"
                  selectedDate={selectedDate}
                  onDateSelect={setSelectedDate}
                  tasksByDate={tasksByDate}
                  tasksForSelectedDate={tasksForSelectedDate}
                  tasksLoading={tasksLoading}
                  onTaskClick={handleTaskClick}
                  onCreateTask={handleCreateTask}
                  onClose={closeTasksPanel}
                />
              </div>
            </>
          )
        }
      />

      {/* Global TaskViewerModal — opens when clicking a task in the panel */}
      <TaskViewerModal
        isOpen={viewerModal.isOpen}
        onClose={closeViewerModal}
        task={viewerModal.task}
        onEdit={handleEditFromViewer}
      />

      {/* Global TaskModals — edit + create */}
      <TaskModal
        isOpen={editTaskModal.isOpen}
        onClose={closeEditTaskModal}
        taskId={editTaskModal.taskId}
        onSave={() => {}}
        showTestAccounts={isITUser && showTestAccounts}
      />
      <TaskModal
        isOpen={createTaskModal.isOpen}
        onClose={closeCreateTaskModal}
        taskId={null}
        onSave={() => {}}
        showTestAccounts={isITUser && showTestAccounts}
      />

      <div className="admin-portal-container">
        <Routes>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route
            path="dashboard"
            element={
              <AdminDashboard
                user={user}
                userData={userData}
                showTestAccounts={showTestAccounts}
              />
            }
          />
          <Route
            path="projects"
            element={
              <AdminProjects
                user={user}
                userData={userData}
                showTestAccounts={showTestAccounts}
              />
            }
          />
          <Route
            path="projects/:projectId"
            element={<AdminProjectDetails user={user} userData={userData} />}
          />
          <Route
            path="clients"
            element={
              <AdminClients
                user={user}
                userData={userData}
                showTestAccounts={showTestAccounts}
              />
            }
          />
          <Route
            path="vendors"
            element={<AdminVendors user={user} userData={userData} showTestAccounts={showTestAccounts} />}
          />
          <Route
            path="calendar"
            element={<AdminCalendar user={user} userData={userData} />}
          />
          <Route
            path="tools"
            element={<AdminTools user={user} userData={userData} />}
          />
          <Route
            path="profile"
            element={
              <AdminProfile
                user={user}
                userData={userData}
                showTestAccounts={showTestAccounts}
              />
            }
          />
          <Route
            path="uploads"
            element={
              <AdminUploads
                user={user}
                userData={userData}
                showTestAccounts={showTestAccounts}
              />
            }
          />
          <Route
            path="settings"
            element={<AdminSettings user={user} userData={userData} />}
          />
        </Routes>
      </div>
      <VersionFooter version={getAdminVersion()} portal="admin" />
    </div>
    </CurrentUserContext.Provider>
  );
}

export default AdminPortal;
